import { type PropsWithChildren, createContext, useContext } from "react";

type SectionSettings = Record<string, unknown>;
type UNSAFE_ASSERTION__SectionSettings<T extends SectionSettings> = T;

const SectionSettingsContext = createContext<SectionSettings | undefined>(
  undefined
);

type Props = {
  value: SectionSettings;
};

function SectionSettingsProvider({
  children,
  value,
}: PropsWithChildren<Props>) {
  return (
    <SectionSettingsContext.Provider value={value}>
      {children}
    </SectionSettingsContext.Provider>
  );
}

function useSectionSettings<
  T extends SectionSettings = Record<string, unknown>
>() {
  const context = useContext(SectionSettingsContext);

  if (context === undefined) {
    throw new Error(
      "useSectionSettings must be used within a SectionSettingsProvider"
    );
  }

  /**
   * @type_assertion_justification
   * TECHNICAL REASON: The context stores generic section settings as `Record<string, unknown>`,
   * but each call site knows the concrete settings shape it provided to the matching
   * `SectionSettingsProvider`.
   * RUNTIME GUARANTEE: The hook throws when no provider is present, and every caller in this
   * codebase pairs `useSectionSettings<T>()` with a provider value of the same section-specific
   * settings object.
   * ALTERNATIVE CONSIDERED: Making the React context itself generic is not possible without
   * pushing the type parameter to every provider instance and losing the shared module-level API.
   * RISK ASSESSMENT: Low - this hook is the single typed boundary for section settings access.
   * MIGRATION_PLAN: Revisit if the app moves to per-section typed context factories.
   */
  return context as UNSAFE_ASSERTION__SectionSettings<T>;
}

export { SectionSettingsProvider, useSectionSettings };
